import type { EditorThemeClasses } from 'lexical';

/**
 * Class names Lexical stamps onto the nodes it renders. These mirror the Froala chrome in
 * `froala-styles.css` (`.fr-view …`) so content authored in either editor looks the same —
 * headings keep `font-medium` rather than `bold`, `strong` is `font-medium` too, and lists are
 * outside-positioned with Froala's 1.5em indent.
 *
 * Kept as Tailwind utility strings (not a stylesheet) so the classes ship with the lib rather
 * than relying on the host app importing another CSS file.
 */
export const lexicalTheme: EditorThemeClasses = {
  paragraph: 'my-0',
  quote: 'border-greyscale-15 ml-2 border-l-2 pl-2',

  heading: {
    h1: 'text-pbk-32 font-medium',
    h2: 'text-pbk-25 font-medium',
    h3: 'text-pbk-20 font-medium',
    h4: 'text-pbk-16 font-medium',
    h5: 'text-pbk-16 font-medium',
    h6: 'text-pbk-16 font-medium',
  },

  list: {
    ol: 'list-decimal list-outside pl-[1.5em]',
    ul: 'list-disc list-outside pl-[1.5em]',
    listitem: 'ml-0',
    // A list nested in a list item must not re-apply the parent's marker box.
    nested: { listitem: 'list-none' },
    checklist: 'list-none pl-0',
    // A box like `Checkbox`'s, drawn in `::before`: `CheckListPlugin` toggles on clicks within its width.
    // `[role=checkbox]` skips items that only wrap a nested list; they get these classes too.
    // `align-baseline` beats the app's global `li > * { vertical-align: middle }`, which drops the text below the strike.
    listitemChecked:
      'relative ml-0 list-none pl-6 line-through focus:outline-none [&>*]:align-baseline [&[role=checkbox]]:before:absolute [&[role=checkbox]]:before:left-0 [&[role=checkbox]]:before:top-[calc((1lh_-_1em)/2)] [&[role=checkbox]]:before:size-[1em] [&[role=checkbox]]:before:cursor-pointer [&[role=checkbox]]:before:rounded-sm [&[role=checkbox]]:before:border-2 [&[role=checkbox]]:before:border-primary-100 [&[role=checkbox]]:before:bg-primary-100 [&[role=checkbox]]:after:absolute [&[role=checkbox]]:after:left-[0.36em] [&[role=checkbox]]:after:top-[calc((1lh_-_1em)/2_+_0.18em)] [&[role=checkbox]]:after:h-[0.5em] [&[role=checkbox]]:after:w-[0.28em] [&[role=checkbox]]:after:rotate-45 [&[role=checkbox]]:after:border-r-2 [&[role=checkbox]]:after:border-b-2 [&[role=checkbox]]:after:border-white [&[role=checkbox]]:after:pointer-events-none',
    listitemUnchecked:
      'relative ml-0 list-none pl-6 focus:outline-none [&>*]:align-baseline [&[role=checkbox]]:before:absolute [&[role=checkbox]]:before:left-0 [&[role=checkbox]]:before:top-[calc((1lh_-_1em)/2)] [&[role=checkbox]]:before:size-[1em] [&[role=checkbox]]:before:cursor-pointer [&[role=checkbox]]:before:rounded-sm [&[role=checkbox]]:before:border-2 [&[role=checkbox]]:before:border-primary-100',
  },

  link: 'text-hyperlink-1 underline cursor-pointer',

  text: {
    // Froala renders bold as `font-medium` (`.fr-view strong`), not `font-bold`.
    bold: 'font-medium',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
    underlineStrikethrough: '[text-decoration:underline_line-through]',
    subscript: 'align-sub text-[0.75em]',
    superscript: 'align-super text-[0.75em]',
    code: 'bg-greyscale-5 rounded px-1 font-mono text-pbk-14',
  },

  code: 'bg-greyscale-5 text-pbk-14 rounded-lg p-2 font-mono text-black block',

  // Fixed layout keeps resized column widths exact; unsized tables split evenly, like Froala
  table: 'w-full table-fixed border-collapse',
  tableRowStriping: '[&_tr:nth-child(even)>td]:bg-greyscale-5',
  tableRow: '',
  tableCell: 'border-greyscale-15 border p-[5px] align-top',
  tableCellHeader: 'border-greyscale-15 text-pbk-16 border bg-greyscale-5 p-[5px] text-left align-top font-medium',
  tableSelected: 'outline-accent-1-100 outline-2',
  tableCellSelected: 'bg-accent-1-35/40',

  hr: 'border-greyscale-15 my-2 border-b',
};
